import React from "react";
import { useOnlinePresence } from "../context/OnlinePresenceContext";

/**
 * Bulina online/offline + label opțional cu ultima activitate.
 * Pass either `personId` (preferat pentru liste angajați) or `userId`.
 *
 * Comportament:
 *  - online → bulină verde pulsantă + label „online" (dacă `label`)
 *  - offline → bulină gri + label „offline" sau „acum X min" (dacă `label`)
 *  - dacă fără `label` și offline → afișează doar bulina gri (vizibilă, dar discretă)
 *
 * Pentru a păstra compatibilitatea cu codul vechi, badge-ul rămâne mereu vizibil acum.
 */
function formatLastSeenShort(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    const now = new Date();
    const diffMin = Math.floor((now - d) / 60000);
    if (diffMin < 1) return "câteva sec.";
    if (diffMin < 60) return `${diffMin}m`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24 && d.toDateString() === now.toDateString()) {
      const hhmm = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
      return `azi ${hhmm}`;
    }
    const y = new Date(now); y.setDate(y.getDate() - 1);
    if (d.toDateString() === y.toDateString()) {
      const hhmm = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
      return `ieri ${hhmm}`;
    }
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `acum ${diffD}z`;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}.${mm}`;
  } catch {
    return null;
  }
}

export function OnlineBadge({ personId, userId, size = 8, label = false, className = "" }) {
  const { isPersonOnline, isUserOnline, details } = useOnlinePresence();
  const online = personId ? isPersonOnline(personId) : userId ? isUserOnline(userId) : false;
  // Găsește last_seen — caută în detalii fie după userId fie după person_id
  let lastSeen = null;
  if (!online) {
    for (const d of Object.values(details || {})) {
      if ((personId && d.person_id === personId) || (userId && d.id === userId)) {
        lastSeen = d.last_seen || null;
        break;
      }
    }
  }
  const lastSeenLabel = !online ? formatLastSeenShort(lastSeen) : null;
  return (
    <span
      className={`inline-flex items-center gap-1 ${className}`}
      title={online ? "Conectat acum" : (lastSeenLabel ? `Offline · ultima activitate: ${lastSeenLabel}` : "Offline")}
      data-testid={online ? `online-badge-${personId || userId}` : `offline-badge-${personId || userId}`}
    >
      <span
        className={`inline-block rounded-full ${online ? "bg-emerald-500 animate-pulse" : "bg-zinc-300"}`}
        style={{ width: size, height: size }}
      />
      {label && (
        <span className={`text-[10px] font-semibold ${online ? "text-emerald-700" : "text-zinc-500"}`}>
          {online ? "online" : (lastSeenLabel ? `offline · ${lastSeenLabel}` : "offline")}
        </span>
      )}
    </span>
  );
}
